import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./modal/ConfirmDialog";
import ImagePreviewOverlay from "./modal/ImagePreviewOverlay";

function ImageThumbnailGallery({
  files,
  onDelete,
  canDelete,
  showFileDetails = true,
}) {
  const { t } = useTranslation();
  const [previewIndex, setPreviewIndex] = useState(null);
  const [confirmFileId, setConfirmFileId] = useState(null);
  const [failedThumbnailIds, setFailedThumbnailIds] = useState(() => new Set());

  const previewFiles = useMemo(
    () =>
      files.filter(
        (file) =>
          file.is_thumbnailable && file.mime_type?.startsWith("image/"),
      ),
    [files],
  );

  if (files.length === 0) {
    return null;
  }

  const renderThumbnail = (file) => {
    const isImage = file.mime_type?.startsWith("image/");
    const isPdf = file.mime_type === "application/pdf";
    const contentUrl = `/v1/files/${file.id}/content`;
    const hasThumbnail =
      file.is_thumbnailable && !failedThumbnailIds.has(file.id);
    const handleThumbnailError = () => {
      setFailedThumbnailIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.add(file.id);
        return nextIds;
      });
    };

    if (isImage && hasThumbnail) {
      const imageIndex = previewFiles.findIndex(
        (previewFile) => previewFile.id === file.id,
      );
      return (
        <button
          type="button"
          className="btn p-0 border-0"
          title={file.name}
          aria-label={t("files.preview_file", { name: file.name })}
          onClick={() => setPreviewIndex(imageIndex)}
        >
          <img
            className="rounded border-end border-bottom"
            style={{ width: "unset", height: "100px" }}
            src={`/v1/files/${file.id}/thumbnails?height=100`}
            alt={file.name}
            title={file.name}
            onError={handleThumbnailError}
          />
        </button>
      );
    }

    if (isPdf && hasThumbnail) {
      return (
        <a
          href={contentUrl}
          target="_blank"
          rel="noreferrer"
          title={file.name}
          className="position-relative d-inline-block"
        >
          <img
            className="rounded border-end border-bottom"
            style={{ width: "unset", height: "100px" }}
            src={`/v1/files/${file.id}/thumbnails?height=100`}
            alt={file.name}
            title={file.name}
            onError={handleThumbnailError}
          />
          <span className="badge bg-danger position-absolute bottom-0 start-0 m-1">
            PDF
          </span>
        </a>
      );
    }

    return (
      <a
        href={contentUrl}
        target="_blank"
        rel="noreferrer"
        title={file.name}
        aria-label={t("files.open_file", { name: file.name })}
        className="d-flex align-items-center justify-content-center rounded border bg-light text-decoration-none"
        style={{ width: "100px", height: "100px" }}
      >
        <i
          className={`fa ${
            isPdf
              ? "fa-file-pdf text-danger"
              : isImage
                ? "fa-file-image text-secondary"
                : "fa-file text-secondary"
          } fa-3x`}
          aria-hidden="true"
        />
      </a>
    );
  };

  return (
    <>
      {files.map((file) => {
        const mayDelete = onDelete && (!canDelete || canDelete(file));
        const contentUrl = `/v1/files/${file.id}/content`;

        return (
          <div
            key={file.id}
            className="d-inline-flex flex-column position-relative align-top me-3 mb-3"
            style={showFileDetails ? { maxWidth: "160px" } : undefined}
          >
            {renderThumbnail(file)}
            {showFileDetails && (
              <div className="d-flex align-items-center gap-1 mt-1">
                <a
                  href={contentUrl}
                  download={file.name}
                  className="small text-truncate flex-grow-1"
                  title={file.name}
                >
                  {file.name}
                </a>
                <a
                  href={contentUrl}
                  download={file.name}
                  className="btn btn-xs btn-default"
                  title={t("files.download")}
                  aria-label={t("files.download_file", { name: file.name })}
                >
                  <i className="fa fa-download" />
                </a>
              </div>
            )}
            {mayDelete && (
              <button
                type="button"
                className="position-absolute d-flex align-items-center justify-content-center border-0 bg-danger text-white rounded-circle thumbnail-delete-badge"
                style={{
                  top: "-6px",
                  right: "-6px",
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                  fontSize: "11px",
                  opacity: 0.5,
                  transition: "opacity 0.15s",
                }}
                title={t("common.delete")}
                aria-label={t("files.delete_file", { name: file.name })}
                onClick={() => setConfirmFileId(file.id)}
              >
                <i className="fa fa-xmark" />
              </button>
            )}
          </div>
        );
      })}

      {previewIndex !== null && (
        <ImagePreviewOverlay
          files={previewFiles}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}

      {confirmFileId !== null && (
        <ConfirmDialog
          title={t("common.delete")}
          message={t("files.confirm_delete")}
          confirmLabel={t("common.delete")}
          confirmClassName="btn btn-danger"
          onCancel={() => setConfirmFileId(null)}
          onConfirm={() => {
            const fileId = confirmFileId;
            setConfirmFileId(null);
            onDelete(fileId);
          }}
        />
      )}
    </>
  );
}

ImageThumbnailGallery.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      is_thumbnailable: PropTypes.bool,
      mime_type: PropTypes.string,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onDelete: PropTypes.func,
  canDelete: PropTypes.func,
  showFileDetails: PropTypes.bool,
};

export default ImageThumbnailGallery;
